
/**
 * Defines layout of a dashboard, as a 2-dimensional grid.
 * */
export interface DashboardLayout {
  /**  Number of columns in the grid */
  columns: number;

  /** Number of rows in the grid */
  rows: number;

  /** Height of a single page of the grid - in rows. */
  pageHeight: number;

  /**
   * Defines layout of each visual within a dashboard.
   * Each entry in this list corresponds to an entry in the list of visuals in the dashboard (by index, after the visuals are ordered).
   * */
  visuals: DashboardVisualLayout[];

  gap: number;
};

export interface DashboardVisualLayout {
  /* Defines x position of a visual (in cells). */
  x: number;

  /* Defines y position of a visual (in cells). */
  y: number;

  /* Defines width of a visual (in cells). */
  sizeX: number;

  /* Defines height of a visual (in cells). */
  sizeY: number;
};

/**
 * Returns the default dashboard layout, that is a 12 x 4 grid.
 * The returned instance does not provide any visual layouts.
 * */
export const GetDefaultDashboardLayout = () => <DashboardLayout>{
  columns: 12,
  rows: 4,
  visuals: []
};

/**
 * Defines style of a dashboard, e.g. margins, padding etc.
 * */
export interface DashboardStyle {
  /**
   * Defines margin between columns of a dashboard.
   * The margin is only applied between columns, that is only for columns followed by another column.
   * */
  columnMargin?: number;

  /**
   * Defines margin between rows of a dashboard.
   * The margin is only applied between rows, that is only for rows followed by another row.
   * */
  rowMargin?: number;

  /**
   * Defines padding inside a dashboard (from its border).
   * When a single number, defines padding for each edge.
   * */
  padding?: number | {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  },

  /**
   * Defines padding inside a visual (from its border).
   * When a single number, defines padding for each edge.
   * */
  visualPadding?: number | {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  },


  edgeWrap?: boolean;

  viewportRightPadding?: number;
  viewportBottomPadding?: number;

  wrapperBottomOffset?: number;
};


/**
 * Defines model for a dashboard layout.
 * */
export interface DashboardLayoutModel {
  name: string;

  /** Width of a dashboard grid, in columns. */
  sizeX: number;

  /** Height of a dashboard grid, in rows. */
  sizeY: number;

  /** Minimum width/height of a visual, in columns. */
  minVisualWidth: number;
  minVisualHeight: number;

  /** List of template sizes a user can select from, when adding a visual. */
  templateSizes: DashboardLayoutTemplateSize[];

  /** Index into templateSizes specifying the deafult size to use for a visual. */
  defaultTemplateSizeIndex: number;

  /** Initial, empty visuals to place in the dashboard, when creating a new one.  */
  initialPlaceholderVisuals: DashboardVisualLayout[];

  chartTypeOptions: Record<string, {
    maxRows?: number;
    maxColumns?: number;
  }>;
};

export interface VisualSize {
  /** Width of this size, in columns. */
  sizeX: number;

  /** Height of this size, in rows. */
  sizeY: number;

  // Temporary properties used by dashboard builder, to pass down the position a visual should be placed at
  // when selecting this size. They are not present in the default size instances.
  x?: number;
  y?: number;
};

/**
 * Defines a template size a user can select from, when adding a visual.
 * */
export interface DashboardLayoutTemplateSize extends VisualSize {
  /** Title of this size, as displayed to the user. */
  title: string;

  /** Whether this size is disabled. A disabled size cannot be selected by the user.  */
  disabled?: boolean;
};

export const DefaultDashboardBuilderTemplateSizes: DashboardLayoutTemplateSize[] = [
  {
    sizeX: 12,
    sizeY: 2,
    title: 'Full Width (12 / 12)'
  },
  {
    sizeX: 8,
    sizeY: 2,
    title: 'Three Quarters (8 / 12)'
  },
  {
    sizeX: 6,
    sizeY: 2,
    title: 'Half (6 / 12)'
  },
  {
    sizeX: 4,
    sizeY: 2,
    title: 'Third (4 / 12)'
  },
  {
    sizeX: 3,
    sizeY: 2,
    title: 'Quarter (3 / 12)'
  },
  {
    sizeX: 2,
    sizeY: 2,
    title: 'Sixth (2 / 12)'
  }
];

export function generateDashboardBuilderLayout(sizeX: number, sizeY: number): DashboardLayoutModel {
  return {
    name: `${sizeX} x ${sizeY}`,
    minVisualWidth:  Math.ceil((sizeX / 12.0) * 2),
    minVisualHeight: Math.ceil((sizeY / 4.0) * 1),
    sizeX: sizeX,
    sizeY: sizeY,
    templateSizes: DefaultDashboardBuilderTemplateSizes,
    defaultTemplateSizeIndex: 3,
    initialPlaceholderVisuals: [
      {
        x: 0,
        y: 0,
        sizeX: Math.ceil((sizeX / 12.0) * 4),
        sizeY: Math.ceil((sizeY / 4.0) * 2)
      }
    ],
    chartTypeOptions: {
      'card': {
        maxColumns: Math.ceil((sizeY / 4.0) * 3),
        maxRows: Math.ceil((sizeY / 4.0) * 1),
      }
    }
  };
}

export interface DashboardBuilderGridSize {
  name: string;
  sizeX: number;
  sizeY: number;
};

export const DefaultDashboardBuilderGridSizes: DashboardBuilderGridSize[] = [
  { name: '12 x 4', sizeX: 12, sizeY: 4 }
];
